/* ===========================================================================
 * WHERE THE FILTERS PANEL LANDS — the arithmetic, with no JSX and no React.
 *
 * Extracted from `FiltersPopover` unchanged. It is a plain function over
 * measurements because that is what it always was: give it the trigger's rect,
 * the panel's unconstrained size and the viewport's, and it answers with the
 * four numbers the panel is styled from. Nothing here touches the DOM, so it can
 * be reasoned about — and tested — without opening a popover.
 *
 * `FiltersPopover` keeps the half that genuinely needs the component: which
 * element is the trigger, when to re-measure, and the remembered natural width.
 * ======================================================================== */

/** Everything the placement depends on, all of it measured by the caller. */
export interface FiltersPopoverMeasurements {
  /** The trigger's viewport rect — the panel hangs off this. */
  trigger: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>;
  /**
   * The panel's height with nothing capping it. `scrollHeight` is what the
   * caller measures, not the rect: the rect is already constrained by the
   * max-height WE set last time, and measuring it would ratchet the panel
   * smaller on every scroll event.
   */
  naturalHeight: number;
  /**
   * The panel's width before anything capped it — the FIRST measurement of an
   * open panel, kept by the caller for the reason the note above gives about
   * `scrollHeight`: measure the constrained box on every pass and a phone
   * rotated back to landscape would keep the width the portrait viewport forced
   * on it, one ratchet per rotation.
   */
  naturalWidth: number;
  /** The trigger's own computed direction, not an assumption about the app. */
  rtl: boolean;
  viewportWidth: number;
  viewportHeight: number;
}

/** The four numbers the panel is styled from. */
export interface FiltersPopoverPosition {
  top: number;
  left: number;
  maxHeight: number;
  maxWidth: number;
}

/* The 8px the panel hangs below the trigger, and the 8px it keeps off the edge
   of the glass. Geometry read at runtime, not a design step — the token layer
   owns the panel's box, not where the viewport ends. */
const GAP = 8;
const EDGE = 8;

export function placeFiltersPopover({
  trigger: t,
  naturalHeight,
  naturalWidth,
  rtl,
  viewportWidth,
  viewportHeight,
}: FiltersPopoverMeasurements): FiltersPopoverPosition {
  /* THE PANEL IS NEVER TALLER THAN THE ROOM IT HAS.
     Below by default; above only when below genuinely does not fit and above is
     roomier — a panel that flips on every near-miss is worse than one that
     occasionally sits low.
     The case that matters is the third one: a long filter set on a laptop fits
     NEITHER way. Left to run past the bottom of the glass its last controls —
     and its Reset — are simply unreachable, with no scrollbar to say so, because
     a `fixed` element does not lengthen the page. So the chosen side's room
     becomes a max-height and the panel scrolls inside itself. */
  const below = viewportHeight - t.bottom - GAP - EDGE;
  const above = t.top - GAP - EDGE;
  const useAbove = naturalHeight > below && above > below;
  // 9rem: below this a filter panel is a scroll-hole rather than a control,
  // and the reader is better served by it overhanging slightly.
  const room = Math.max(144, useAbove ? above : below);
  const height = Math.min(naturalHeight, room);
  const top = useAbove
    ? Math.max(EDGE, t.top - GAP - height)
    : Math.min(t.bottom + GAP, Math.max(EDGE, viewportHeight - EDGE - height));
  /* AND NEVER WIDER THAN THE GLASS.
     The class sets 316px, capped at `--popover-max` — a WIDTH ROLE, which is a
     statement about how wide a panel of filters should be and not a claim that
     the reader's screen is that wide. On a narrow phone, a 280px foldable or a
     desktop at 200% zoom it is wider than the viewport, and the `left` clamp
     below cannot rescue a box that does not fit: whichever edge it is pushed
     against, the other one runs off the glass and takes a control with it. So
     the room is measured the same way the height is, and the panel is told its
     ceiling; `--popover-max` still wins whenever there is room for it. */
  const width = Math.min(naturalWidth, Math.max(0, viewportWidth - EDGE * 2));
  // Aligned to the trigger's INLINE-START edge, so under Arabic the panel drops
  // from the other side — what `start-0` used to do for us.
  let left = rtl ? t.right - width : t.left;
  left = Math.min(Math.max(EDGE, left), Math.max(EDGE, viewportWidth - width - EDGE));
  return { top, left, maxHeight: room, maxWidth: width };
}
